import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import {
  capitalize,
  invoiceBadgeClass,
  invoiceHref,
  formatDate,
  formatMoney,
  groupByRoom,
} from './badgeUtils';

export default function ItemsTab({ items, projectId, roomFilter }) {
  if (items.length === 0) {
    return <p className="cp-empty">No items yet.</p>;
  }

  const groups = groupByRoom(items, roomFilter);

  return (
    <div>
      {groups.map(([room, roomItems]) => (
        <div key={room} className="cp-room-group">
          <p className="cp-room-heading">{room}</p>
          {roomItems.map((item) => (
            <ItemCard key={item.itemId} item={item} projectId={projectId} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ItemCard({ item, projectId }) {
  const inv = item.invoice;
  const isPaid = inv && (inv.remainingBalance != null ? inv.remainingBalance <= 0 : inv.status === 'paid');
  const navigate = useNavigate();
  const goToDetail = item.itemId
    ? () => navigate(`/client/projects/${projectId}/items/${item.itemId}`)
    : undefined;

  return (
    <div
      className={`cp-item-card${goToDetail ? ' clickable' : ''}`}
      onClick={goToDetail}
      role={goToDetail ? 'button' : undefined}
      tabIndex={goToDetail ? 0 : undefined}
      onKeyDown={
        goToDetail
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') goToDetail();
            }
          : undefined
      }
    >
      <div className="cp-item-img">
        {item.imageUrl ? <img src={item.imageUrl} alt="" /> : null}
      </div>

      <div className="cp-item-info">
        <p className="cp-item-name">{item.name}</p>
        {item.specs.length > 0 && (
          <ul className="cp-item-specs">
            {item.specs.map((spec, i) => (
              <li key={i}>{spec}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="cp-item-pricing">
        <span className="cp-item-pricing-label">QTY:</span>
        <span className="cp-item-pricing-value">{item.qty}</span>
        <span className="cp-item-pricing-label">Item Price:</span>
        <span className="cp-item-pricing-value">{formatMoney(item.unitPrice)}</span>
        <span className="cp-item-pricing-label">Sales Tax:</span>
        <span className="cp-item-pricing-value">{formatMoney(item.tax)}</span>
        <span className="cp-item-pricing-label">Shipping:</span>
        <span className="cp-item-pricing-value">{formatMoney(item.shipping)}</span>
        <span className="cp-item-pricing-label">Total Price:</span>
        <span className="cp-item-pricing-value bold">{formatMoney(item.totalPrice)}</span>
        {inv && (
          <>
            <span className="cp-item-pricing-label">Payment:</span>
            <span className="cp-item-pricing-value">{formatMoney(inv.totalPaid)}</span>
          </>
        )}
      </div>

      <div className="cp-item-invoice">
        {inv ? (
          <>
            <span className={`cp-badge ${invoiceBadgeClass(inv.status)}`}>
              {isPaid ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={12} /> Invoiced
                </span>
              ) : (
                capitalize(inv.status)
              )}
            </span>
            <p className="cp-item-invoice-meta">
              Invoice #{inv.invoiceNumber || inv.invoiceId}
              {inv.invoiceDate && (
                <>
                  <br />
                  Issued on {formatDate(inv.invoiceDate)}
                </>
              )}
            </p>
            {!isPaid && <PayInvoiceButton inv={inv} projectId={projectId} />}
          </>
        ) : (
          <span className="cp-badge cp-badge-gray">Not Invoiced</span>
        )}
      </div>
    </div>
  );
}

function PayInvoiceButton({ inv, projectId }) {
  const { href, isExternal } = invoiceHref(inv, projectId);
  const props = {
    className: 'cp-btn-primary',
    style: { padding: '7px 16px', fontSize: 13 },
    onClick: (e) => e.stopPropagation(),
  };

  return isExternal ? (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      Pay Invoice
    </a>
  ) : (
    <Link to={href} {...props}>
      Pay Invoice
    </Link>
  );
}
